import { shortcut } from '../ui/responsive';
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { hexA, type Item } from '@scribbleplot/shared';
import { color, font, shadow, STICKY_PALETTE } from '../tokens';
import { useCamera } from './camera';
import { useBoard, type MenuState } from './store';

/**
 * Right-click menu.
 *
 * Contents depend on what was clicked: bare canvas offers insertions, a single
 * item offers type-specific controls (sticky colour, text size), and a
 * multi-selection offers the set operations. Placement is clamped so the menu
 * never opens off-screen.
 */

const MENU_WIDTH = 208;
const ROW_HEIGHT = 34;

export type Row =
  | { kind: 'button'; label: string; shortcut?: string; danger?: boolean; run: () => void }
  | { kind: 'divider' }
  | { kind: 'swatches'; current: string; pick: (color: string) => void }
  | { kind: 'sizes'; current: 's' | 'm' | 'l'; pick: (size: 's' | 'm' | 'l') => void };

export function ContextMenu({ menu, accent }: { menu: MenuState; accent: string }) {
  const board = useBoard();
  const ref = useRef<HTMLDivElement | null>(null);
  /** Whatever had focus when the menu opened, so it can be handed back. */
  const returnFocus = useRef<HTMLElement | null>(null);

  /** The menu's focusable items, in visual order. */
  const items = (): HTMLButtonElement[] =>
    ref.current ? [...ref.current.querySelectorAll<HTMLButtonElement>('button:not([disabled])')] : [];

  const dismiss = useCallback(() => {
    board.setMenu(null);
    // Returning focus matters most for keyboard users: without it, focus falls
    // back to the document and the next Tab starts from the top of the page.
    returnFocus.current?.focus?.();
  }, [board]);

  // Move focus into the menu on open, and remember where it came from.
  useEffect(() => {
    returnFocus.current = document.activeElement as HTMLElement | null;
    items()[0]?.focus();
  }, []);

  useEffect(() => {
    const onDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) board.setMenu(null);
    };

    /**
     * Menu semantics: arrows move between items and wrap, Home/End jump to the
     * ends, Escape closes. Enter and Space activate natively, since the items
     * are real buttons.
     */
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss();
        return;
      }
      const focusable = items();
      if (!focusable.length) return;
      const current = focusable.indexOf(document.activeElement as HTMLButtonElement);

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = (current + step + focusable.length) % focusable.length;
        focusable[current === -1 ? 0 : next].focus();
      } else if (event.key === 'Home') {
        event.preventDefault();
        focusable[0].focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        focusable[focusable.length - 1].focus();
      } else if (event.key === 'Tab') {
        // A menu is a single stop: tabbing out closes it rather than walking
        // through every entry.
        dismiss();
      }
    };

    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [board, dismiss]);

  const close = (run: () => void) => () => {
    run();
    board.setMenu(null);
  };

  const rows = buildEditorActions(menu, board, close);
  if (!rows.length) return null;

  const estimatedHeight = rows.length * ROW_HEIGHT + 30;
  const left = Math.max(12, Math.min(menu.sx, window.innerWidth - MENU_WIDTH - 12));
  const top = Math.min(menu.sy, Math.max(12, window.innerHeight - estimatedHeight));

  return (
    <div
      ref={ref}
      role="menu"
      onContextMenu={(event) => event.preventDefault()}
      style={{
        position: 'fixed',
        left,
        top,
        zIndex: 9999,
        background: color.panel,
        border: `1px solid rgba(255,255,255,.1)`,
        borderRadius: 12,
        padding: 5,
        minWidth: 200,
        boxShadow: shadow.panel,
        animation: 'sp-pop-in .12s ease',
      }}
    >
      {rows.map((row, index) => (
        <MenuRow key={index} row={row} accent={accent} />
      ))}
    </div>
  );
}

export function MenuRow({
  row,
  accent,
  itemRole = 'menuitem',
}: {
  row: Row;
  accent: string;
  itemRole?: 'menuitem' | 'button';
}): ReactNode {
  if (row.kind === 'divider') {
    return <div aria-hidden style={{ height: 1, background: 'rgba(255,255,255,.08)', margin: '5px 6px' }} />;
  }

  if (row.kind === 'swatches') {
    return (
      <div style={{ display: 'flex', gap: 7, padding: '7px 10px' }}>
        {STICKY_PALETTE.map((swatch) => (
          <button
            key={swatch}
            type="button"
            role={itemRole}
            aria-label={`Sticky colour ${swatch}`}
            onClick={() => row.pick(swatch)}
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: swatch,
              cursor: 'pointer',
              border: row.current === swatch ? '2px solid #fff' : '2px solid rgba(255,255,255,.18)',
              padding: 0,
            }}
          />
        ))}
      </div>
    );
  }

  if (row.kind === 'sizes') {
    return (
      <div style={{ display: 'flex', gap: 4, padding: '6px 10px' }}>
        {(['s', 'm', 'l'] as const).map((size) => {
          const active = row.current === size;
          return (
            <button
              key={size}
              type="button"
              role={itemRole}
              aria-label={`Text size ${size.toUpperCase()}`}
              aria-pressed={active}
              onClick={() => row.pick(size)}
              style={{
                width: 30,
                height: 26,
                borderRadius: 7,
                border: `1px solid ${active ? accent : 'rgba(255,255,255,.15)'}`,
                background: active ? hexA(accent, 0.15) : 'transparent',
                color: active ? accent : '#B9BCC7',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              {size.toUpperCase()}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <button
      type="button"
      role={itemRole}
      onClick={row.run}
      onPointerEnter={(event) => (event.currentTarget.style.background = color.hover)}
      onPointerLeave={(event) => (event.currentTarget.style.background = 'transparent')}
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: '7px 10px',
        border: 'none',
        background: 'transparent',
        color: row.danger ? color.danger : color.textStrong,
        fontSize: 13,
        fontFamily: 'inherit',
        borderRadius: 8,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span>{row.label}</span>
      <span
        aria-hidden="true"
        style={{ fontFamily: font.mono, fontSize: 10, color: '#B1B5C3', marginLeft: 'auto', paddingLeft: 18 }}
      >
        {row.shortcut ? shortcut(row.shortcut) : ''}
      </span>
    </button>
  );
}

export function buildEditorActions(
  menu: MenuState,
  board: ReturnType<typeof useBoard.getState>,
  close: (run: () => void) => () => void,
): Row[] {
  const rows: Row[] = [];
  if (!board.canEdit) return rows;
  const button = (label: string, run: () => void, shortcut?: string, danger?: boolean) =>
    rows.push({ kind: 'button', label, shortcut, danger, run: close(run) });

  // Bare canvas: insertion point is where the click landed.
  if (!menu.id) {
    button('Add sticky note', () => board.addSticky(menu.wx, menu.wy), 'N');
    button('Add text', () => board.addText(menu.wx, menu.wy), 'T');
    button('Add link card', () => board.addLink(menu.wx, menu.wy), 'L');
    rows.push({ kind: 'divider' });
    button('Select all', () => board.selectAll(), '⌘A');
    button('Zoom to fit', () => useCamera.getState().fit(board.doc), '⌘1');
    return rows;
  }

  if (menu.isStroke && board.sel.length === 1) {
    button('Duplicate', () => board.duplicateSel(), '⌘D');
    button('Delete drawing', () => board.deleteSel(), '⌫', true);
    return rows;
  }

  const sel = board.sel;
  if (sel.length > 1) {
    const groupable = sel.filter((id) => {
      const item = board.itemById(id);
      return item && item.type !== 'frame' && item.type !== 'group';
    });
    if (groupable.length > 1) button(`Group ${groupable.length} items`, () => board.groupSel(), '⌘G');
    button('Duplicate', () => board.duplicateSel(), '⌘D');
    button('Bring to front', () => board.bringToFront());
    button('Send to back', () => board.sendToBack());
    rows.push({ kind: 'divider' });
    button(`Delete ${sel.length} items`, () => board.deleteSel(), '⌫', true);
    return rows;
  }

  const item = board.itemById(menu.id);
  if (!item) return rows;

  if (item.type === 'sticky') {
    rows.push({
      kind: 'swatches',
      current: item.color,
      pick: (swatch) => {
        board.updateItem(item.id, { color: swatch } as Partial<Item>);
        board.setMenu(null);
      },
    });
    rows.push({ kind: 'divider' });
  }

  if (item.type === 'text') {
    rows.push({
      kind: 'sizes',
      current: item.size,
      pick: (size) => {
        board.updateItem(item.id, { size, textStyle: { ...item.textStyle, fontSize: undefined } } as Partial<Item>);
        board.setMenu(null);
      },
    });
    rows.push({ kind: 'divider' });
  }

  if (item.type === 'group') button('Ungroup', () => board.ungroup(item.id), '⇧⌘G');

  if (item.type === 'sticky' || item.type === 'text' || item.type === 'link' || item.type === 'frame') {
    button(item.type === 'frame' ? 'Rename area' : 'Edit text', () => board.setEditing(item.id));
  }

  button('Duplicate', () => board.duplicateSel(), '⌘D');
  button('Bring to front', () => board.bringToFront());
  button('Send to back', () => board.sendToBack());
  rows.push({ kind: 'divider' });
  button('Delete', () => board.deleteSel(), '⌫', true);

  return rows;
}
